import { ItemsPerPage } from '@billy/design-system'
import { useMemo } from 'react'

import { InvoiceDocumentType } from '@features/invoices/enums/invoiceDocumentType'
import { InvoicesSortProperty } from '@features/invoices/enums/invoicesSortProperty'
import { useFetchSalesDocuments } from '@features/invoices/hooks/useFetchSalesDocuments'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { SortDirection } from '../../../../../enums/sortDirection'

const getNormalizedDayDate = (date: Date) => {
  const dayDate = new Date(date)
  dayDate.setHours(0, 0, 0, 0)

  return dayDate
}

const getLatestDate = (dates: (Date | undefined)[]) => {
  return dates
    .filter((date): date is Date => date !== undefined)
    .reduce<Date | undefined>((latest, current) => (!latest || current > latest ? current : latest), undefined)
}

interface UseInvoiceIssueDateLimitsProps {
  invoiceNumberPrefix?: string | null | undefined
  isInvoice: boolean
}

export const useInvoiceIssueDateLimits = (
  { invoiceNumberPrefix, isInvoice }: UseInvoiceIssueDateLimitsProps = { isInvoice: false },
) => {
  const { organization } = useUserOrganization()
  const { documents } = useFetchSalesDocuments({
    documentNumberPrefix: invoiceNumberPrefix || undefined,
    documentTypes: [InvoiceDocumentType.Invoice],
    documentsListType: 'invoices',
    isEnabled: isInvoice,
    page: 1,
    pageSize: 1 as ItemsPerPage,
    sortDirection: SortDirection.Desc,
    sortProperty: InvoicesSortProperty.Date,
  })

  const fiscalLockingDate = useMemo(
    () =>
      organization?.fiscalLockingDate ? getNormalizedDayDate(new Date(organization.fiscalLockingDate)) : undefined,
    [organization?.fiscalLockingDate],
  )

  const latestInvoiceIssueDate = useMemo(() => {
    const latestInvoice = documents?.[0]

    return isInvoice && latestInvoice ? getNormalizedDayDate(new Date(latestInvoice.issueDate)) : undefined
  }, [documents, isInvoice])

  const minIssueDate = useMemo(
    () => getLatestDate([fiscalLockingDate, latestInvoiceIssueDate]),
    [fiscalLockingDate, latestInvoiceIssueDate],
  )

  return {
    fiscalLockingDate,
    latestInvoiceIssueDate,
    minIssueDate,
  }
}
